import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ComparableTableDataSource, PropertyMap, SecondarySearchFilter } from '../interface/order.interface';

@Injectable({
  providedIn: 'root',
})
export class SalesApproachState {
  constructor() {}

  /** Stores a list of comparable properties in an active order */
  private _comparableListSub$ = new BehaviorSubject<Array<ComparableTableDataSource>>([]);
  comparableList$ = this._comparableListSub$.asObservable();

  get comparableList(): Array<ComparableTableDataSource> {
    return this._comparableListSub$.value;
  }

  set comparableList(order: Array<ComparableTableDataSource>) {
    this._comparableListSub$.next(order);
  }

  /**
   * Stores the criteria to be used to get comparables.
   * This state should be updated every time a sub-filter, pagination or sort happens on the comparables.
   * This will trigger the /getComparables API based on the saved criteria.
   */
  private _secondarySearchFilterSub$ = new BehaviorSubject<SecondarySearchFilter | null | undefined>(null);
  secondarySearchFilter$ = this._secondarySearchFilterSub$.asObservable();

  get secondarySearchFilter(): SecondarySearchFilter | null | undefined {
    return this._secondarySearchFilterSub$.value;
  }

  set secondarySearchFilter(subFilter: Partial<SecondarySearchFilter | null | undefined>) {
    if (subFilter !== null) {
      const concatenatedValue = {
        ...this._secondarySearchFilterSub$.value,
        ...subFilter,
      };
      this._secondarySearchFilterSub$.next(concatenatedValue);
    } else {
      this._secondarySearchFilterSub$.next(null);
    }
  }

  /**
   * Stores total length of comparable properties in an active order
   * This is to be mainly used for pagination.
   */
  private _comparableListLengthSub$ = new BehaviorSubject<number>(0);
  comparableListLength$ = this._comparableListLengthSub$.asObservable();

  get comparableListLength(): number {
    return this._comparableListLengthSub$.value;
  }

  set comparableListLength(length: number) {
    this._comparableListLengthSub$.next(length);
  }

  /**
   * Stores complete list of selected comparable properties in an active order just like selectedComparableList$.
   * However it stores the data in a map format for faster read/write operations.
   * Only use this state when you need to read/write selected comparable properties.
   */
  private _selectedComparableMapSub$ = new BehaviorSubject<PropertyMap>({});
  selectedComparableMap$ = this._selectedComparableMapSub$.asObservable();

  get selectedComparableMap(): PropertyMap {
    return this._selectedComparableMapSub$.value;
  }

  set selectedComparableMap(value: PropertyMap) {
    this._selectedComparableMapSub$.next(value);
  }

  /**
   * Stores the comparable order returned by /getSelectedComparables.
   * Used for display ordering when the active report's salesComparison
   * has `selectedComparableSortBy` set.
   */
  private _orderedSelectedComparableIdsSub$ = new BehaviorSubject<Array<string>>([]);
  orderedSelectedComparableIds$ = this._orderedSelectedComparableIdsSub$.asObservable();

  get orderedSelectedComparableIds(): Array<string> {
    return this._orderedSelectedComparableIdsSub$.value;
  }

  set orderedSelectedComparableIds(ids: Array<string>) {
    this._orderedSelectedComparableIdsSub$.next(ids);
  }
}
